import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import AddIcon from '~/assets/svg/newSvg/actions/add.svg?react';
import { Action } from '~/ui/Actions';
import { useEffect, useRef, useState } from 'react';
import { AssetStorageFolders } from './AssetStorageFolders/AssetStorageFolders';
import { IconButton, Spinner } from '~/ui';
import FileIcon from '~/assets/svg/newSvg/file-plus.svg?react';
import FolderIcon from '~/assets/svg/newSvg/folder-plus.svg?react';
import UploadIcon from '~/assets/svg/newSvg/upload-file.svg?react';
import { Modal } from '~/components/Shared/Modal/Modal';
import { FolderCreateForm } from '~/components/Storage/FolderCreateForm/FolderCreateForm';
import { useToastSuccess } from '~/utils/useToastSuccess';
import styles from './AssetStorage.module.scss';
import StorageSubmitInfo from '~/components/Storage/StorageSubmitInfo/StorageSubmitInfo';
import { useAuthentication } from '~/components/Authentication/useAuthentication';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import {
  AssetStorageFiles,
  IFileProps,
} from './AssetStorageFiles/AssetStorageFiles';

interface IDownloadFile {
  name: string;
  file: File;
}

interface IAsset {
  id: number;
  name: string;
}

export const AssetStorage = () => {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const perPage = 100;
  const { assetId, folderId } = useParams();
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isAddingFiles, setIsAddingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState<IFileProps | null>(null);
  const [currentAsset, setCurrentAsset] = useState<IAsset>({ id: 0, name: '' });
  const [downloadFiles, setDownloadFiles] = useState<File[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const client = useQueryClient();

  const { data: assets } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/assets', {
        params: { perPage },
      });
      return data;
    },
    queryKey: ['assets'],
    onError: error => {
      toastError(error);
    },
  });

  const handleCloseFolderModal = () => {
    setIsFolderModalOpen(!isFolderModalOpen);
    setIsActionOpen(false);
  };

  const {
    data: asset,
    isLoading,
    refetch,
  } = useQuery<IAsset>({
    queryFn: async () => {
      const { data } = await instance.get(`admin/assets/${assetId}`);
      return data;
    },
    queryKey: ['asset', assetId],
    onError: error => {
      toastError(error);
    },
  });

  const { mutateAsync: createFolder, isLoading: isFolderCreateLoading } =
    useMutation(
      async (value: string) => {
        const response = await instance.post('admin/folders', {
          name: value,
          asset_id: assetId,
          parent_id: folderId,
        });
        return response;
      },
      {
        onSuccess: () => {
          toastSuccess(t('folder_created_successfully'));
          setIsFolderModalOpen(false);
          setIsActionOpen(false);
          client.invalidateQueries({ queryKey: ['asset'], exact: false });
          client.invalidateQueries({
            queryKey: ['asset-folders'],
            exact: false,
          });
        },
        onError: error => {
          toastError(error);
        },
      }
    );

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDrag(true);
  };

  const { mutateAsync: addFiles } = useMutation(
    async (data: {
      asset_id: number | undefined;
      files: IDownloadFile[];
      folder_id: string | undefined;
    }) => {
      const response = await instance.postForm('admin/documents', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ['documents'], exact: false });
        toastSuccess(t('files_successfully_added'));
        setIsActionOpen(false);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const handleDragLeav = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDrag(false);
  };

  const fileDownloadClick = () => {
    inputRef?.current?.click();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setDownloadFiles(files);
    setIsAddingFiles(false);
    setIsModalOpen(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    setDownloadFiles(files);
    setIsAddingFiles(false);
    setIsModalOpen(true);
  };

  const handleCloseBtn = () => {
    setDownloadFiles([]);
    setIsModalOpen(false);
    setIsAddingFiles(false);
  };

  const handleSubmit = (value: string) => {
    createFolder(value);
    handleCloseFolderModal();
  };

  const handleFilesSubmit = () => {
    const filesData = downloadFiles.map(file => ({
      name: file.name,
      file: file,
    }));

    const props = {
      asset_id: asset?.id,
      files: filesData,
      folder_id: folderId,
    };

    addFiles(props);
    setIsAddingFiles(false);
    setIsModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dragWrapper = document.getElementById('dragWrapper');
      const actionButton = document.querySelector('.action');

      if (
        dragWrapper &&
        !dragWrapper.contains(event.target as Node) &&
        actionButton &&
        !actionButton.contains(event.target as Node)
      ) {
        setIsAddingFiles(false);
        setIsActionOpen(false);
      }
    };

    if (isAddingFiles) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAddingFiles]);

  useEffect(() => {
    setIsActionOpen(false);
  }, [assetId, folderId]);

  return (
    <Page
      className={styles.pageContainer}
      heading={
        <Heading
          text={`${t('asset_storage')} "${((asset && assetId) || !isLoading) && asset?.name
            }"`}
          classNameText={styles.text}
          actions={
            <div className={styles.actionWrapper}>
              {assetId && (
                <Action
                  variant="success"
                  onClick={() => setIsActionOpen(!isActionOpen)}
                  className="action"
                >
                  <AddIcon />
                  {t('create')}
                </Action>
              )}
              {isActionOpen && (
                <div className={styles.actionListWrapper}>
                  <IconButton
                    className={styles.actionBtn}
                    onClick={() => setIsFolderModalOpen(true)}
                  >
                    <FolderIcon className={styles.btnIcon} />
                    {t('create_folder')}
                  </IconButton>
                  {folderId && (
                    <IconButton
                      className={styles.actionBtn}
                      onClick={() => setIsAddingFiles(true)}
                    >
                      <FileIcon className={styles.btnIcon} />
                      {t('upload_file')}
                    </IconButton>
                  )}
                </div>
              )}
            </div>
          }
        />
      }
    >
      <div className={styles.mainContent}>
        <AssetStorageFolders
          refetchAsset={refetch}
          assetId={assetId}
          folderId={folderId}
        />
        {folderId && (
          <AssetStorageFiles
            folderId={folderId}
            setSelectedFile={setSelectedFile}
          />
        )}
        {isAddingFiles && (
          <div
            id="dragWrapper"
            className={styles.dragWrapper}
            onDragStart={e => handleDragStart(e)}
            onDragLeave={e => handleDragLeav(e)}
            onDragOver={e => handleDragStart(e)}
            onDrop={e => handleDrop(e)}
            onClick={fileDownloadClick}
          >
            <div>
              <UploadIcon />
            </div>
            {!drag ? (
              <>
                <p className={styles.dragTitle}>{t('drop_files_into_area')}</p>
                <input
                  type="file"
                  ref={inputRef}
                  onChange={handleChange}
                  multiple
                  className={styles.inputFile}
                />
              </>
            ) : (
              <p className={styles.dragTitle}>
                {t('files_in_the_visible_area')}
              </p>
            )}
            <p className={styles.dragDescription}>
              {t('downloaded_files_info')}:{' '}
            </p>
            <div className={styles.folderName}>
              <FolderIcon />
              <span>{currentAsset?.name}</span>
            </div>
          </div>
        )}
      </div>

      <Modal modalStyles={styles.selectedFileModal} isOpen={!!selectedFile} onClose={() => setSelectedFile(null)}>
        <div className={styles.selectedFileWrapper}>
          <img src={selectedFile?.full_path} alt={selectedFile?.name} className={styles.selectedFileImage} />
        </div>
      </Modal>

      <Modal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
      >
        {isFolderCreateLoading ? (
          <Spinner />
        ) : (
          <FolderCreateForm
            onClick={handleCloseFolderModal}
            onSubmit={handleSubmit}
          />
        )}
      </Modal>
      <Modal
        modalStyles={styles.photosModal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <StorageSubmitInfo
          handleCloseBtn={handleCloseBtn}
          handleSubmit={handleFilesSubmit}
          allAssets={isSuperAdmin ? assets?.data : []}
          setCurrentAsset={setCurrentAsset}
          currentAsset={asset || { id: 0, name: '' }}
        />
      </Modal>
    </Page>
  );
};
